import { useCallback, useEffect, useRef, useState } from "react";
import { CalendarCheck, QrCode, Search, Hash } from "lucide-react";
import { api } from "../../../lib/api";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import { StepHeader } from "../../../components/kiosk/StepHeader";
import { QrScanner } from "../../../components/kiosk/QrScanner";

export default function InviteSearchStep({ onSelect }) {
  const [mode, setMode] = useState("scan");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [scanError, setScanError] = useState("");
  const [looking, setLooking] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [codeError, setCodeError] = useState("");
  const [codeLooking, setCodeLooking] = useState(false);
  const lookupInFlight = useRef(false);

  useEffect(() => {
    if (mode !== "manual") {
      setResults([]);
      return;
    }
    setSearching(true);
    const handle = setTimeout(() => {
      api
        .get("/invites/search", { params: { q: query } })
        .then(({ data }) => setResults(data))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [query, mode]);

  const handleDetect = useCallback((token) => {
    if (lookupInFlight.current) return;
    lookupInFlight.current = true;
    setLooking(true);
    setScanError("");
    api
      .get(`/invites/by-token/${token}`)
      .then(({ data }) => onSelect(data))
      .catch((err) => {
        setScanError(err.response?.data?.detail || "QR code not recognized");
        lookupInFlight.current = false;
        setLooking(false);
        setTimeout(() => setScanError(""), 3000);
      });
  }, [onSelect]);

  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (!codeInput.trim()) return;
    setCodeLooking(true);
    setCodeError("");
    api
      .get(`/invites/by-code/${codeInput.trim()}`)
      .then(({ data }) => onSelect(data))
      .catch((err) => {
        setCodeError(err.response?.data?.detail || "Code not recognized");
        setCodeLooking(false);
      });
  };


  return (
    <div className="max-w-xl mx-auto space-y-6" data-testid="checkin-invite-search-step">
      <StepHeader
        icon={CalendarCheck}
        title="Find Pre-Registered Visitor"
        subtitle={mode === "scan" ? "Point the camera at the visitor's QR code" : mode === "code" ? "Enter the 5-digit code from the invite email" : "Search by name or phone number"}
      />
      <div className="flex gap-2 justify-center">
        <button
          type="button"
          onClick={() => setMode("scan")}
          data-testid="prereg-mode-scan-button"
          className={`flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm font-bold uppercase tracking-wide border ${
            mode === "scan" ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-500 border-slate-300"
          }`}
        >
          <QrCode size={16} /> Scan QR
        </button>
        <button
          type="button"
          onClick={() => setMode("manual")}
          data-testid="prereg-mode-manual-button"
          className={`flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm font-bold uppercase tracking-wide border ${
            mode === "manual" ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-500 border-slate-300"
          }`}
        >
          <Search size={16} /> Search Manually
        </button>
        <button
          type="button"
          onClick={() => setMode("code")}
          data-testid="prereg-mode-code-button"
          className={`flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm font-bold uppercase tracking-wide border ${
            mode === "code" ? "bg-brand-navy text-white border-brand-navy" : "bg-white text-slate-500 border-slate-300"
          }`}
        >
          <Hash size={16} /> Enter Code
        </button>
      </div>

      {mode === "scan" ? (
        <div className="bg-white border border-slate-200 shadow-sm rounded-lg p-6 space-y-4 text-center">
          <QrScanner onDetect={handleDetect} paused={looking} onCameraError={() => setMode("manual")} />
          {looking && <p className="text-brand-cyan text-sm font-semibold" data-testid="qr-scan-looking">Checking invite...</p>}
          {scanError && (
            <p className="text-red-600 text-sm font-semibold" data-testid="qr-scan-error">
              {scanError}
            </p>
          )}
          <p className="text-xs text-slate-400">Hold the visitor's QR code steady inside the frame</p>
        </div>
      ) : mode === "code" ? (
        <form onSubmit={handleCodeSubmit} className="bg-white border border-slate-200 shadow-sm rounded-lg p-6 space-y-4 text-center">
          <Input
            data-testid="invite-code-input"
            placeholder="e.g. 48213"
            inputMode="numeric"
            maxLength={5}
            autoFocus
            value={codeInput}
            onChange={(e) => {
              setCodeInput(e.target.value.replace(/\D/g, ""));
              setCodeError("");
            }}
            className="h-16 text-center text-2xl tracking-[0.3em] rounded-sm bg-white border-slate-300"
          />
          {codeError && (
            <p className="text-red-600 text-sm font-semibold" data-testid="invite-code-error">
              {codeError}
            </p>
          )}
          <Button
            type="submit"
            disabled={!codeInput.trim() || codeLooking}
            data-testid="invite-code-submit-button"
            className="w-full h-14 rounded-sm bg-brand-navy text-white hover:bg-brand-navy-light font-bold uppercase text-sm"
          >
            {codeLooking ? "Checking..." : "Find Visitor"}
          </Button>
          <p className="text-xs text-slate-400">Use this if the visitor's QR code won't scan — the same code is shown in their invite email</p>
        </form>
      ) : (
        <div className="bg-white border border-slate-200 shadow-sm rounded-lg p-6 space-y-4">
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="invite-search-input"
              placeholder="Start typing a visitor name or phone..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              className="h-14 pl-11 rounded-sm bg-white border-slate-300 text-base sm:text-lg w-full"
            />
          </div>
          {searching && (
            <p className="text-sm text-slate-400 text-center" data-testid="invite-search-loading">
              Searching...
            </p>
          )}
          {results.length > 0 && (
            <div className="border border-slate-200 divide-y divide-slate-100" data-testid="invite-search-results">
              {!query.trim() && (
                <p className="text-xs uppercase tracking-widest text-slate-400 p-3 bg-slate-50">
                  Today's Expected Visitors
                </p>
              )}
              {results.map((inv) => (
                <button
                  type="button"
                  key={inv.id}
                  onClick={() => onSelect(inv)}
                  data-testid={`invite-result-${inv.id}`}
                  className="w-full text-left p-4 hover:bg-sky-50 flex items-center justify-between"
                >
                  <span>
                    <span className="font-bold text-brand-navy">{inv.visitor_name}</span>{" "}
                    <span className="text-slate-400 text-sm">
                      · {inv.purpose} · hosted by {inv.host_name}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}
          {!searching && query.trim() && results.length === 0 && (
            <p className="text-sm text-slate-400 text-center" data-testid="invite-search-empty">
              No pending invites found for "{query}"
            </p>
          )}
          {!searching && !query.trim() && results.length === 0 && (
            <p className="text-sm text-slate-400 text-center" data-testid="invite-search-empty">
              No visitors expected right now
            </p>
          )}
        </div>
      )}
    </div>
  );
}
